<template>
  <div class="printer-editor">
    <AppMapPointPanelHeader :header="header" @close="$emit('close')" />
    <div class="printer-editor__content">
      <b-form>
        <slot name="floor"></slot>
        <b-form-group label="Printer photo">
          <div :class="{ 'with-photo': printerTemp.avatar_id }">
            <app-avatar v-if="printerTemp.avatar_id" :src="avatarSrc" :size="avatarSize" />
            <app-file-upload
              ref="uploader"
              @upload="onUpload"
              @error="onUploadError"
              :post-url="avatarUrl"
              auto-upload
              :short-view="Boolean(printerTemp.avatar_id)"
              accept="image/png,image/gif,image/jpeg,image/webp"
              :size="1024 * 1024 * 2"
              :extensions="allowedExtensions"
              :short-view-message="printerPhotoTitle"
            />
          </div>
        </b-form-group>
        <b-form-group label="Name*" :state="$v.printerTemp.name.required">
          <b-input type="text" placeholder="Name" v-model="$v.printerTemp.name.$model" />
          <template #invalid-feedback v-if="$v.printerTemp.name.$dirty && !$v.printerTemp.name.required">Printer name required </template>
        </b-form-group>
        <b-form-group label="Model">
          <b-input type="text" placeholder="printer model" v-model="$v.printerTemp.model.$model" />
        </b-form-group>
        <b-form-group label="Asset number">
          <b-input type="text" v-model="$v.printerTemp.asset_num.$model" />
        </b-form-group>
        <b-form-group label="Note">
          <b-textarea placeholder="add some note..." v-model="$v.printerTemp.description.$model" />
        </b-form-group>
        <b-form-group label="Install URL">
          <b-input type="text" v-model="$v.printerTemp.install_url.$model" />
        </b-form-group>

        <b-form-group label="Department" :state="!printerTemp.department_id && $v.departmentTemp.name.requiredIf">
          <b-input-group v-if="printerTemp.department_id">
            <b-input v-model="$v.departmentTemp.name.$model" />
            <template #append>
              <b-btn
                @click="
                  printerTemp.department_id = null
                  departmentsByTerm = []
                "
                ><i class="ion ion-md-close"></i
              ></b-btn>
            </template>
          </b-input-group>
          <app-select
            v-else
            @search="departmentSearch"
            @select="departmentSelect"
            ref="departmentSelect"
            :proposeNew="can(PN.DEPARTMENT_CREATE)"
            :items="departmentsByTerm"
            placeholder="select department"
          >
            <template #default="props">
              <span class="select-item">
                <span class="select-item-text">{{ props.item.name }}</span>
                <b-btn size="sm" class="align-self-center" @click.stop.prevent="departmentDestroy_(props.item.id)">
                  <i class="ion ion-md-trash"></i
                ></b-btn>
              </span>
            </template>
          </app-select>
          <template #invalid-feedback v-if="printerTemp.department_id && !$v.departmentTemp.name.requiredIf">
            Department name required
          </template>
        </b-form-group>

        <div class="btn-panel">
          <b-btn type="button" v-if="can(PN.PRINTER_DELETE) && !isNew" variant="default" @click="destroy" title="Delete" class="mr-auto">
            <i class="ion ion-md-trash"></i
          ></b-btn>
          <div class="ml-auto"></div>
          <b-btn type="button" variant="primary" @click="save">Save</b-btn>
          <b-btn type="button" variant="default" @click="cancel">Cancel</b-btn>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import nanoid from 'nanoid'
import AppSelect from '@/components/shared/AppSelect'
import AppFileUpload from '@/components/shared/AppFileUpload'
import { PRINTER_CREATE, PRINTER_UPDATE } from '@/store/modules/printer'
import Id, { isLocalId } from '@/utils/Id'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import * as PN from '@/utils/PermissionNames'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { getAvatarSrc } from '@/utils/Helpers'
import AppAvatar from '@/components/shared/AppAvatar'
import AppMapPointPanelHeader from '@/components/shared/AppMapPointPanelHeader.vue'
import { getItemHeader } from '@/components/shared/helpers'
import escape from 'lodash/escape'

export default {
  name: 'PrinterEditor',
  components: {
    AppMapPointPanelHeader,
    AppAvatar,
    AppSelect,
    AppFileUpload
  },
  props: {
    id: {
      type: [Number, String],
      required: true
    },
    point: {
      type: Object,
      required: true
    }
  },
  validations() {
    return {
      printerTemp: {
        name: {
          required
        },
        model: {},
        asset_num: {},
        description: {},
        install_url: {},
        department_id: {}
      },
      departmentTemp: {
        name: {
          requiredIf: requiredIf(() => this.printerTemp.department_id)
        }
      }
    }
  },
  data: () => ({
    PN,
    printerTemp: {
      name: '',
      model: '',
      asset_num: '',
      description: '',
      install_url: '',
      department_id: '',
      avatar_updated_at: '',
      avatar_id: ''
    },
    departmentTemp: {
      name: ''
    },
    departmentsByTerm: [],
    allowedExtensions: 'gif,jpg,jpeg,png,webp'
  }),
  computed: {
    ...mapGetters('printer', ['getPrinter']),
    ...mapGetters('department', ['departments', 'getDepartment']),
    ...mapGetters('rbac', ['can']),
    header() {
      return getItemHeader(this.point, 'Printer')
    },
    avatarSize() {
      return this.printerTemp.avatar_id ? '100px' : '50px'
    },
    avatarSrc() {
      return getAvatarSrc(this.printerTemp.avatar_id, this.printerTemp.avatar_updated_at)
    },
    avatarUrl() {
      let suffix = this.printerTemp.avatar_id ? `/${this.printerTemp.avatar_id}` : ''
      return `/api/v1/avatars${suffix}`
    },
    department() {
      return this.getDepartment(this.printerTemp.department_id) || {}
    },
    isNew() {
      return isLocalId(this.printerTemp.id)
    },
    printerPhotoTitle() {
      return this.printerTemp.avatar_id ? 'click to change printer photo' : 'click to upload printer photo'
    }
  },
  methods: {
    ...mapActions('printer', ['printerSave', 'printerLoad']),
    ...mapActions('position', ['positionSave', 'positionDestroy']),
    ...mapActions('department', ['departmentSave', 'departmentDestroy']),
    ...mapActions('point', ['pointSave']),
    ...mapMutations('printer', {
      printerCreate_: PRINTER_CREATE,
      printerUpdate_: PRINTER_UPDATE
    }),
    departmentSelect(p) {
      if (typeof p === 'object') {
        this.printerTemp.department_id = p.id
      } else if (typeof p === 'string') {
        // is a new
        this.departmentSave({ id: Id(), name: p }).then((pId) => {
          this.printerTemp.department_id = pId
        })
      } else {
        throw new Error("Can't resolve returned company type")
      }
    },
    departmentSearch(term) {
      this.departmentsByTerm = []
      this.$nextTick(() => {
        this.departmentsByTerm = term
          ? this.departments.filter((department) => department.name && department.name.indexOf(term) !== -1)
          : this.departments
      })
    },
    departmentDestroy_(id) {
      const department = this.getDepartment(id)
      this.$appToast.question(`A you sure want to delete '${escape(department.name)}' department?`).then(() =>
        this.departmentDestroy(department).then(() => {
          this.$refs.departmentSelect.search('')
        }))
    },
    save() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        const save = () => {
          const pointData = { id: this.point.id, title: this.printerTemp.name }
          const pointPromise = this.pointSave(pointData)

          pointPromise.then((pointId) => {
            this.printerTemp.point_id = pointId
            const payload = { printer: this.printerTemp, config: { params: { expand: 'point' } } }
            this.printerSave(payload).then(() => {
              this.$emit('save', { pointId })
            })
          })
        }

        if (this.printerTemp.department_id) {
          this.departmentSave(this.departmentTemp).then((departmentId) => {
            this.printerTemp.department_id = departmentId
            save()
          })
        } else {
          save()
        }
      }
    },
    cancel() {
      this.$emit('cancel')
    },
    destroy() {
      this.$appToast.question('A you sure want to delete printer?').then(() => {
        this.$emit('destroy')
      })
    },
    onUpload(avatar) {
      this.printerTemp.avatar_id = avatar.id
      this.printerTemp.avatar_updated_at = nanoid()
      this.$appToast.success('Printer photo uploaded!')
    },
    onUploadError({ cause, response }) {
      let errorMessages = []
      if (cause === 'denied' && response.length) {
        response.forEach((error) => {
          errorMessages.push(error.message)
        })
      } else if (cause === 'size') {
        errorMessages.push('File is too big. Its size cannot exceed 2 MiB.')
      } else if (cause === 'extension') {
        errorMessages.push(`File extension is not allowed. Support ${this.allowedExtensions}.`)
      } else {
        errorMessages.push(`Unknown error on file upload. Please write to support.`)
      }
      this.$appToast.error(errorMessages.map(escape).join('<br>'))
    }
  },
  watch: {
    id: {
      immediate: true,
      handler(id) {
        this.printerTemp = Object.assign({ id }, this.printerTemp, this.getPrinter(id))
      }
    },
    department: {
      immediate: true,
      handler(value) {
        this.departmentTemp = Object.assign({}, this.departmentTemp, value)
      }
    }
  }
}
</script>

<style lang="scss">
.printer-editor__content {
  padding: 24px;
}

.select-item {
  display: flex;
  justify-items: left;

  .btn {
    margin-left: auto;
  }
}

.with-photo {
  display: flex;
  align-items: center;

  & .file-upload {
    margin-left: 20px;
    max-width: 7em;
  }
}
</style>
